import { Injectable, inject, DestroyRef } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { filter, take, catchError } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserPreferencesCacheService } from './user-preferences-cache.service';
import { ProfileProviderPFStateService } from './profile-provider-pf-state.service';
import { ProfileProviderPJStateService } from './profile-provider-pj-state.service';
import { PlanStatusInfo, AccessPlanControllerService, UserRef } from '../../core/generated';
import { Feature, hasFeature, getAvailableFeatures } from '../config/package-features.config';

// Após regenerar a API, PlanStatusInfo já terá cpfCnpj
export type PlanStatusCacheInfo = PlanStatusInfo;

/**
 * Service centralizado para gerenciamento de status de planos e controle de acesso baseado em features.
 * 
 * IMPORTANTE: Um CPF/CNPJ pode ter MÚLTIPLOS planos. Este service consolida as features de todos
 * os planos ativos para determinar o acesso.
 * 
 * Métodos principais:
 * - canAccessFeature(cpfCnpj, feature): Verifica acesso considerando TODOS os planos ativos
 * - hasFeatureAccess(cpfCnpj, feature): Versão simplificada que retorna apenas boolean
 * - findAllByCpfCnpj(cpfCnpj): Retorna TODOS os planos (ativos ou não) de um CPF/CNPJ
 * - findActivePlansByCpfCnpj(cpfCnpj): Retorna apenas planos ativos
 * - getConsolidatedFeatures(cpfCnpj): Retorna Set com TODAS as features disponíveis
 * - getPlansSummary(cpfCnpj): Retorna resumo dos planos (útil para debug)
 * 
 * Filtros aplicados:
 * - Apenas planos com packageType !== null são considerados
 * - Planos expirados são filtrados
 * - Planos com pendências de pagamento são filtrados
 * - Planos com allowsAccess=false são filtrados
 */

export interface AccessControl {
  hasAccess: boolean;
  reason?: 'no-plan' | 'payment-pending' | 'expired' | 'no-permission' | 'inactive';
  planStatus?: PlanStatusCacheInfo;
}

@Injectable({ providedIn: 'root' })
export class PlanStatusService {
  private readonly UserPreferencesCacheService = inject(UserPreferencesCacheService);
  private readonly profilePFState = inject(ProfileProviderPFStateService);
  private readonly profilePJState = inject(ProfileProviderPJStateService);
  private readonly accessPlansService = inject(AccessPlanControllerService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly planStatusesSubject = new BehaviorSubject<PlanStatusCacheInfo[]>([]);
  public readonly planStatuses$ = this.planStatusesSubject.asObservable();

  // flags and in-flight promises to avoid duplicate backend calls
  private loaded = false;
  private refreshPromise: Promise<void> | null = null;

  // Per-cpf/cnpj fetch caching (in-flight + resolved cache)
  private readonly fetchPromises = new Map<string, Promise<PlanStatusCacheInfo[]>>();
  private readonly fetchCache = new Map<string, PlanStatusCacheInfo[]>();

  // Memoization caches for expensive computations
  private readonly consolidatedFeaturesCache = new Map<string, Set<Feature>>();
  private readonly activePlansCache = new Map<string, PlanStatusCacheInfo[]>();
  private cacheVersion = 0; // Increment to invalidate all memoization caches

  constructor() {
    // React to ALL profile changes (including null = removido) para atualizar o sidebar
    this.profilePFState.profileProviderPF$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fetchCache.clear(); // garante dados frescos do servidor
        this.refresh().catch(() => { });
      });

    this.profilePJState.profileProviderPJ$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fetchCache.clear(); // garante dados frescos do servidor
        this.refresh().catch(() => { });
      });
  }

  // Alias to refresh
  async loadForCurrentUser(): Promise<void> {
    await this.refresh();
  }

  // Refresh plan statuses for current user and related PJ profiles
  async refresh(): Promise<void> {
    // If there's already an in-flight refresh, return it so callers share the same request
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      const all: PlanStatusCacheInfo[] = [];

      // Get user (may be null)
      let user: UserRef | null = null;
      try {
        user = await firstValueFrom(this.UserPreferencesCacheService.getUser());
      } catch (err) {
        user = null;
      }

      // PF
      if (user && (user as any).cpf) {
        try {
          const pf = await this.fetchForCpfCnpj((user as any).cpf);
          if (pf?.length) {
            all.push(...pf);
          }
        } catch (err) {
          console.error('PlanStatusService: error fetching PF plan statuses', err);
        }
      }

      // PJ profiles from state service
      try {
        const profilesPJ = await Promise.race<any[] | null>([
          firstValueFrom(this.profilePJState.profileProviderPJ$.pipe(filter(p => p !== null), take(1))).catch(() => null),
          new Promise(resolve => setTimeout(() => resolve(null), 1500))
        ]);

        if (profilesPJ?.length) {
          const promises = profilesPJ
            .filter(p => p && (p as any).cnpj)
            .map(p => this.fetchForCpfCnpj((p as any).cnpj));

          const results = await Promise.allSettled(promises);
          results.forEach(result => {
            if (result.status === 'fulfilled' && result.value?.length) {
              all.push(...result.value);
            }
          });
        }
      } catch (err) {
        // Silently ignore errors
      }

      // Emit and invalidate caches
      this.planStatusesSubject.next(all);
      this.invalidateMemoizationCaches();
      this.loaded = true;
    })();

    try {
      await this.refreshPromise;
    } finally {
      // clear in-flight promise so future refreshes can run again
      this.refreshPromise = null;
    }
  }

  /**
   * Retorna o primeiro plano encontrado por CPF/CNPJ
   * @deprecated Use findAllByCpfCnpj para considerar múltiplos planos
   */
  findByCpfCnpj(cpfCnpj: string): PlanStatusCacheInfo | null {
    if (!cpfCnpj) return null;
    const list = this.planStatusesSubject.getValue();
    return list.find(p => p?.cpfCnpj === cpfCnpj) ?? null;
  }

  /**
   * Retorna TODOS os planos para um CPF/CNPJ específico
   * Filtra apenas planos com packageType não nulo
   */
  findAllByCpfCnpj(cpfCnpj: string): PlanStatusCacheInfo[] {
    if (!cpfCnpj) return [];
    const list = this.planStatusesSubject.getValue();
    return list.filter(p =>
      p?.cpfCnpj === cpfCnpj &&
      p.packageType != null
    );
  }

  /**
   * Retorna apenas os planos ATIVOS para um CPF/CNPJ específico
   * Considera planos com allowsAccess=true, packageType não nulo, sem expiração/pendências
   */
  findActivePlansByCpfCnpj(cpfCnpj: string): PlanStatusCacheInfo[] {
    if (!cpfCnpj) return [];

    // Check memoization cache
    const cacheKey = `${cpfCnpj}-${this.cacheVersion}`;
    const cached = this.activePlansCache.get(cacheKey);
    if (cached) return cached;

    const allPlans = this.findAllByCpfCnpj(cpfCnpj);
    const activePlans = allPlans.filter(plan =>
      plan.allowsAccess &&
      !this.isPlanExpired(plan) &&
      !this.hasPaymentPending(plan)
    );

    // Store in cache
    this.activePlansCache.set(cacheKey, activePlans);
    return activePlans;
  }

  /**
   * Consolida todas as features disponíveis de TODOS os planos ativos de um CPF/CNPJ
   * Retorna um Set único de features disponíveis
   */
  getConsolidatedFeatures(cpfCnpj: string): Set<Feature> {
    if (!cpfCnpj) return new Set<Feature>();

    // Check memoization cache
    const cacheKey = `${cpfCnpj}-${this.cacheVersion}`;
    const cached = this.consolidatedFeaturesCache.get(cacheKey);
    if (cached) return cached;

    const activePlans = this.findActivePlansByCpfCnpj(cpfCnpj);
    const allFeatures = new Set<Feature>();

    activePlans.forEach(plan => {
      if (plan.packageType) {
        const planFeatures = getAvailableFeatures(plan.packageType);
        planFeatures.forEach(feature => allFeatures.add(feature));
      }
    });

    // Store in cache
    this.consolidatedFeaturesCache.set(cacheKey, allFeatures);
    return allFeatures;
  }

  // Fetch plan statuses for any cpf/cnpj on demand with per-identifier caching and in-flight de-dup
  async fetchForCpfCnpj(cpfCnpj: string): Promise<PlanStatusCacheInfo[]> {
    if (!cpfCnpj) return [];

    // Return resolved cache if available
    const cached = this.fetchCache.get(cpfCnpj);
    if (cached) return cached;

    // If there's an in-flight request for this cpfCnpj, return it
    const inFlight = this.fetchPromises.get(cpfCnpj);
    if (inFlight) return inFlight;

    const p = firstValueFrom(
      this.accessPlansService.getAllPlanStatuses(cpfCnpj).pipe(
        catchError(err => {
          console.error('PlanStatusService: error fetching for cpfCnpj', cpfCnpj, err);
          return of([]);
        })
      )
    ).then(res => {
      const r = res || [];
      this.fetchCache.set(cpfCnpj, r);
      return r;
    }).finally(() => {
      // remove the in-flight marker so future calls can re-fetch if needed
      this.fetchPromises.delete(cpfCnpj);
    });

    this.fetchPromises.set(cpfCnpj, p);
    return p;
  }

  // Clear all cached fetch results
  clearCache(): void {
    this.fetchCache.clear();
    this.fetchPromises.clear();
    this.invalidateMemoizationCaches();
    this.planStatusesSubject.next([]);
    this.loaded = false;
  }

  // Clear cache for a specific cpf/cnpj
  clearCacheFor(cpfCnpj: string): void {
    if (!cpfCnpj) return;
    this.fetchCache.delete(cpfCnpj);
    this.fetchPromises.delete(cpfCnpj);
    this.invalidateMemoizationCaches();
  }

  // Invalidate all memoization caches
  private invalidateMemoizationCaches(): void {
    this.cacheVersion++;
    this.consolidatedFeaturesCache.clear();
    this.activePlansCache.clear();
  }

  // Returns the current list of PlanStatusInfo synchronously (from BehaviorSubject)
  getAllPlanStatuses(): PlanStatusCacheInfo[] {
    return this.planStatusesSubject.getValue();
  }

  // Returns the current list as a Promise (useful in async flows)
  async getAllPlanStatusesAsync(): Promise<PlanStatusCacheInfo[]> {
    return this.getAllPlanStatuses();
  }

  /**
   * Verifica se o plano está ativo (permite acesso)
   * Considera QUALQUER plano ativo para o CPF/CNPJ
   */
  isPlanActive(cpfCnpj: string): boolean {
    return this.findActivePlansByCpfCnpj(cpfCnpj).length > 0;
  }

  /**
   * Verifica se o plano está expirado
   */
  isPlanExpired(plan: PlanStatusCacheInfo | null): boolean {
    if (!plan || !plan.allowsAccess) return true;
    if (plan.expiresAt) {
      return new Date(plan.expiresAt) < new Date();
    }
    return false;
  }

  /**
   * Verifica se há pendência de pagamento
   */
  hasPaymentPending(plan: PlanStatusCacheInfo | null): boolean {
    if (!plan) return false;
    return (plan.daysOverdue != null && plan.daysOverdue > 0) ||
      (plan.pendingAmount != null && plan.pendingAmount > 0);
  }

  /**
   * Verifica se o plano tem um dos tipos de pacote necessários
   * @deprecated Use canAccessFeature com Feature enum ao invés de packageTypes
   */
  hasPackageType(cpfCnpj: string, requiredTypes: string[]): boolean {
    const plan = this.findByCpfCnpj(cpfCnpj);
    if (!plan?.packageType) return false;
    return requiredTypes.some(type => plan.packageType!.includes(type));
  }

  /**
   * Verifica se pode acessar uma feature específica usando o sistema de Features
   * Considera TODOS os planos ativos do CPF/CNPJ e consolida as features
   */
  canAccessFeature(cpfCnpj: string, feature?: Feature | Feature[]): AccessControl {
    if (!cpfCnpj) {
      return { hasAccess: false, reason: 'no-plan' };
    }

    const allPlans = this.findAllByCpfCnpj(cpfCnpj);

    // Sem plano
    if (allPlans.length === 0) {
      return { hasAccess: false, reason: 'no-plan' };
    }


    // Filtra planos ativos (sem expiração/pendência)
    const activePlans = this.findActivePlansByCpfCnpj(cpfCnpj);

    // Todos os planos estão expirados
    if (activePlans.length === 0) {
      const hasExpired = allPlans.some(plan => this.isPlanExpired(plan));
      const hasPending = allPlans.some(plan => this.hasPaymentPending(plan));
      const hasInactive = allPlans.some(plan => !plan.allowsAccess);

      if (hasExpired) {
        return { hasAccess: false, reason: 'expired', planStatus: allPlans[0] };
      }
      if (hasPending) {
        return { hasAccess: false, reason: 'payment-pending', planStatus: allPlans[0] };
      }
      if (hasInactive) {
        return { hasAccess: false, reason: 'inactive', planStatus: allPlans[0] };
      }
      return { hasAccess: false, reason: 'inactive', planStatus: allPlans[0] };
    }

    // Se nenhuma feature foi especificada, apenas verifica se tem plano ativo
    if (!feature) {
      return { hasAccess: true, planStatus: activePlans[0] };
    }

    // Consolida features de todos os planos ativos
    const consolidatedFeatures = this.getConsolidatedFeatures(cpfCnpj);

    // Verifica acesso à(s) feature(s)
    const features = Array.isArray(feature) ? feature : [feature];
    const hasPermission = features.some(f => consolidatedFeatures.has(f));

    if (!hasPermission) {
      return { hasAccess: false, reason: 'no-permission', planStatus: activePlans[0] };
    }

    return { hasAccess: true, planStatus: activePlans[0] };
  }

  /**
   * Verifica se tem acesso a uma feature específica (retorna apenas boolean)
   */
  hasFeatureAccess(cpfCnpj: string, feature: Feature | Feature[]): boolean {
    return this.canAccessFeature(cpfCnpj, feature).hasAccess;
  }

  /**
   * Retorna todas as features disponíveis para o CPF/CNPJ
   * Consolida features de todos os planos ativos
   */
  getAvailableFeatures(cpfCnpj: string): Feature[] {
    const consolidatedFeatures = this.getConsolidatedFeatures(cpfCnpj);
    return Array.from(consolidatedFeatures);
  }

  /**
   * Retorna o tipo de perfil baseado no packageType
   * Considera o primeiro plano ativo encontrado
   */
  getProfileType(cpfCnpj: string): string {
    const activePlans = this.findActivePlansByCpfCnpj(cpfCnpj);
    if (activePlans.length === 0) return '';

    const packageType = activePlans[0].packageType;
    if (!packageType) return '';

    // Provider PF
    if (packageType.includes('PROVIDER_PF')) {
      return 'Prestadores PF';
    }

    // Lab
    if (packageType.includes('LAB')) {
      return 'Laboratório PJ';
    }

    // Provider + Contractor PJ
    if (packageType.includes('PROVIDER_CONTRACTOR_PJ')) {
      return 'Prestadores e Contratantes PJ';
    }

    // Provider PJ
    if (packageType.includes('PROVIDER_PJ')) {
      return 'Prestadores PJ';
    }

    // Contractor PJ
    if (packageType.includes('CONTRACTOR_PJ')) {
      return 'Contratantes PJ';
    }

    // Administrator
    if (packageType.includes('ADMINISTRATOR')) {
      return 'Administrador';
    }

    return '';
  }

  /**
   * Verifica se é um perfil Provider (PF ou PJ) - tem acesso a SERVICE_OFFERING ou RESOURCE_OFFERING
   */
  isProvider(cpfCnpj: string): boolean {
    return this.hasFeatureAccess(cpfCnpj, [Feature.SERVICE_OFFERING, Feature.RESOURCE_OFFERING]);
  }

  /**
   * Verifica se é um perfil Contractor - tem acesso a SERVICE_DEMAND ou RESOURCE_DEMAND
   */
  isContractor(cpfCnpj: string): boolean {
    return this.hasFeatureAccess(cpfCnpj, [Feature.SERVICE_DEMAND, Feature.RESOURCE_DEMAND]);
  }

  /**
   * Retorna todos os planos ativos (de todos os CPF/CNPJ)
   */
  getActivePlans(): PlanStatusCacheInfo[] {
    return this.planStatusesSubject.getValue().filter(p =>
      p?.allowsAccess &&
      p?.packageType != null
    );
  }

  /**
   * Verifica se o usuário tem algum plano ativo com acesso a pesquisas avançadas
   */
  hasAnyActivePlan(): boolean {
    const activePlans = this.getActivePlans();
    return activePlans.some(plan =>
      plan.packageType && hasFeature(plan.packageType, Feature.ADVANCED_SEARCH)
    );
  }

  /**
   * Verifica se o usuário tem pelo menos um plano ativo (independente de features)
   */
  hasAnyPlan(): boolean {
    return this.getActivePlans().length > 0;
  }

  /**
   * Retorna informações resumidas sobre os planos de um CPF/CNPJ
   * Útil para debugging e exibição de informações
   */
  getPlansSummary(cpfCnpj: string): {
    total: number;
    active: number;
    expired: number;
    pending: number;
    features: Feature[];
    packageTypes: (string | undefined)[];
  } {
    const allPlans = this.findAllByCpfCnpj(cpfCnpj);
    const activePlans = this.findActivePlansByCpfCnpj(cpfCnpj);
    const features = this.getAvailableFeatures(cpfCnpj);

    return {
      total: allPlans.length,
      active: activePlans.length,
      expired: allPlans.filter(p => this.isPlanExpired(p)).length,
      pending: allPlans.filter(p => this.hasPaymentPending(p)).length,
      features: features,
      packageTypes: allPlans
        .map(p => p.packageType)
        .filter(pt => pt != null)
    };
  }
}
